import { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Settings,
  User,
  LogOut,
  ToggleRight,
  Type,
  Palette,
  MessageSquare,
} from "lucide-react";
import { useAppAuth } from "@/hooks/useAppAuth";
import { useNavigate } from "react-router";
import { useAdminMode } from "../../contexts/AdminModeContext";
import { useColor } from "../../contexts/ColorContext";
import { appConfig } from "@/appConfig";
import { Language, useI18n } from "@/contexts/I18nContext";

interface SettingsDropdownProps {
  onOpenProfileModal: () => void;
  currentUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null;
}

const SettingsDropdown = ({
  onOpenProfileModal,
  currentUser,
}: SettingsDropdownProps) => {
  const { role, signOut } = useAppAuth();
  const { adminMode, setAdminMode, toggleAdminMode } = useAdminMode();
  const { themeColor, setThemeColor, logoName, setLogoName } = useColor();
  const { language, setLanguage, t } = useI18n();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLogoChanger, setShowLogoChanger] = useState(false);
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [newLogoName, setNewLogoName] = useState(logoName);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    signOut();
    setIsOpen(false);
  };

  const handleProfileImage = () => {
    onOpenProfileModal();
    setIsOpen(false);
  };

  const handleModeSwitch = () => {
    const newMode = adminMode === "admin" ? "member" : "admin";
    toggleAdminMode();

    // Navigate to appropriate page based on new mode
    if (newMode === "member") {
      navigate("/assistant");
    } else {
      navigate("/documents");
    }
    setIsOpen(false);
  };

  const handleColorChange = (color: string) => {
    setThemeColor(color);
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
    setShowLogoChanger(false);
    setShowLanguagePicker(false);
  };

  const toggleLogoChanger = () => {
    setShowLogoChanger(!showLogoChanger);
    setShowColorPicker(false);
    setShowLanguagePicker(false);
    setNewLogoName(logoName);
  };

  const toggleLanguagePicker = () => {
    setShowLanguagePicker(!showLanguagePicker);
    setShowColorPicker(false);
    setShowLogoChanger(false);
  };

  const handleLogoNameChange = () => {
    if (newLogoName.trim()) {
      setLogoName(newLogoName.trim());
      setShowLogoChanger(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowColorPicker(false);
        setShowLogoChanger(false);
        setShowLanguagePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Settings Button */}
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 rounded-full border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t("common.settings")}
        title={t("common.settings")}
      >
        <Settings className="w-4 h-4" />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
          {/* User Info */}
          {currentUser && (
            <>
              <div className="px-4 py-2 text-sm text-gray-900 border-b border-gray-100">
                <div className="font-medium">
                  {currentUser.first_name && currentUser.last_name
                    ? `${currentUser.first_name} ${currentUser.last_name}`
                    : currentUser.email}
                </div>
                <div className="text-gray-500 text-xs">{currentUser.email}</div>
              </div>
            </>
          )}

          {/* Profile Option */}
          <button
            onClick={handleProfileImage}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
          >
            <User className="w-4 h-4 text-gray-500" />
            {t("common.profile")}
          </button>

          {/* Wissensassistent Option */}
          <button
            onClick={() => {
              setIsOpen(false);
              // Switch to member mode if admin/superadmin, then navigate with delay
              if ((role === "admin" || role === "superadmin") && adminMode === "admin") {
                setAdminMode("member");
                // Small delay to let mode change propagate before navigation
                setTimeout(() => navigate("/assistant"), 50);
              } else {
                navigate("/assistant");
              }
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
          >
            <MessageSquare className="w-4 h-4 text-gray-500" />
            {t("settingsDropdown.memberAssistant")}
          </button>

          <div className="relative">
            <button
              onClick={toggleLanguagePicker}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
            >
              <Type className="w-4 h-4 text-gray-500" />
              {t("settingsDropdown.language")}
              <span className="ml-auto text-xs text-gray-400">
                {t(`language.${language}`)}
              </span>
            </button>

            {showLanguagePicker && (
              <div className="absolute right-full top-0 mr-1 bg-white rounded-md shadow-lg border border-gray-200 p-3 z-50 min-w-[220px]">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  {t("language.label")}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(["de", "en", "fr", "it"] as Language[]).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setLanguage(option)}
                      className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                        language === option
                          ? "border-slate-400 bg-slate-100 text-slate-900"
                          : "border-slate-200 bg-white text-gray-700 hover:bg-slate-50"
                      }`}
                    >
                      {t(`language.${option}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Logo Name Changer */}
          {appConfig.features.isDemoMode ? (
            <div className="relative">
              <button
                onClick={toggleLogoChanger}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
              >
                <Type className="w-4 h-4 text-gray-500" />
                {t("settingsDropdown.logoName")}
                <span
                  className="ml-auto text-xs text-gray-400 truncate max-w-[80px]"
                  title={logoName}
                >
                  {logoName}
                </span>
              </button>

              {/* Logo Name Changer Dropdown */}
              {showLogoChanger && (
                <div className="absolute right-full top-0 mr-1 bg-white rounded-md shadow-lg border border-gray-200 p-3 z-50 min-w-[200px]">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {t("settingsDropdown.logoName")}
                  </div>
                  <input
                    type="text"
                    value={newLogoName}
                    onChange={(e) => setNewLogoName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                    placeholder={t("settingsDropdown.newLogoName")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleLogoNameChange();
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleLogoNameChange}
                      className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 transition-colors hover:bg-slate-50"
                    >
                      {t("common.save")}
                    </button>
                    <button
                      onClick={() => setShowLogoChanger(false)}
                      className="flex-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Color Theme Option */}
          {appConfig.features.isDemoMode ? (
            <div className="relative">
              <button
                onClick={toggleColorPicker}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
              >
                <Palette className="w-4 h-4 text-gray-500" />
                {t("settingsDropdown.color")}
                <div
                  className="w-4 h-4 rounded-full border border-gray-300 ml-auto"
                  style={{ backgroundColor: themeColor }}
                />
              </button>

              {/* Color Picker Dropdown */}
              {showColorPicker && (
                <div className="absolute right-full top-0 mr-1 bg-white rounded-md shadow-lg border border-gray-200 p-3 z-50">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {t("settingsDropdown.chooseColor")}
                  </div>
                  <input
                    type="color"
                    value={themeColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-full h-10 rounded border border-gray-300 cursor-pointer"
                  />
                </div>
              )}
            </div>
          ) : null}

          {/* Mode Switch Option - Only show "Zu Admin" when in member mode */}
          {(role === "admin" || role === "superadmin") && adminMode === "member" && (
            <button
              onClick={handleModeSwitch}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
            >
              <ToggleRight className="w-4 h-4 text-gray-500" />
              {t("settingsDropdown.switchToAdmin")}
            </button>
          )}

          {/* Divider */}
          <div className="border-t border-gray-100 my-1" />

          {/* Logout Option */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors text-left bg-transparent border-none focus:outline-none active:outline-none"
          >
            <LogOut className="w-4 h-4 text-gray-500" />
            {t("common.logout")}
          </button>
        </div>
      )}
    </div>
  );
};

export default SettingsDropdown;
